<template>
  <div>
    <template v-if="preloader">
      <client-only>
        <v-lottie position="fixed" />
      </client-only>
    </template>

    <div class="display-flex flex-column justify-content-center align-items-center gap-20">
      <div class="display-flex justify-content-center align-items-center">
        <nuxt-img
          src="/assets/images/svg/ic_activation-code.svg"
          alt="Airalo"
          :height="`125px`"
          :width="`125px`"
          class="w-auto"
          loading="lazy"
        />
      </div>

      <div class="display-flex justify-content-center">
        <i18n path="scenes.otp.description" tag="p" class="typo-p color-text-secondary text-center mb-0">
          <template #email>
            <a :href="`mailto:${email}`" target="_blank" class="color-text-primary" rel="noopener">
              {{ email }}
            </a>
          </template>
        </i18n>
      </div>

      <form @submit.prevent="" autocomplete="off" class="w-100">
        <div class="display-flex flex-column jusify-content-center align-items-center gap-35">
          <div
            class="display-flex justify-content-center align-items-center gap-10"
            :dir="$dir() === 'rtl' ? 'auto' : false"
          >
            <VieOtpInput
              ref="otpInput"
              input-classes="input general-otp-field"
              separator=""
              input-type="text"
              :num-inputs="4"
              :should-auto-focus="true"
              :is-input-num="true"
              @on-complete="handleOnComplete"
            />
          </div>

          <div class="w-100 display-flex justify-content-center">
            <button class="button button-secondary-outline small" @click.prevent="resendActivation">
              {{ $t('scenes.verify.activation.code-resend') }}
            </button>
          </div>

          <div class="w-100 mt-5">
            <button
              class="button button-secondary block btn-primary-hv"
              data-testid="continue-button"
              :disabled="preloader"
              @click.prevent="activateUser"
            >
              {{ $t('global.continue') }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
  import VieOtpInput from '~/components/input/otp-input.vue'

  export default {
    components: {
      VieOtpInput,
    },

    data() {
      return {
        code: '',
        highlight: '',
        email: null,
        currentActivationCode: '',
        preloader: false,
      }
    },

    directives: {
      upper: {
        update(el, bindings, node) {
          el.value = String(el.value).toUpperCase()
        },
      },
    },

    methods: {
      handleOnComplete(value) {
        this.code = value
        this.submitInput()
      },

      setEmail() {
        if (this.$route.query.email) {
          this.email = this.$route.query.email
        } else if (
          this.$store.getters.get_auth_dialogs_email &&
          this.$store.getters.get_auth_dialogs_email.length > 0
        ) {
          this.email = this.$store.getters.get_auth_dialogs_email
        } else if (window?.localStorage?.getItem('set_auth_dialogs_email')) {
          this.email = window.localStorage.getItem('set_auth_dialogs_email')
        } else {
          this.email = null
        }

        if (this.email) {
          this.currentEmail = this.email

          return this.currentEmail
        } else {
          this.$router.push('/auth/login')
          return false
        }
      },

      setCodeAndActiveUserFromQuery() {
        const code = this.$route.query.activation

        if (code?.length) {
          this.$refs.otpInput.setOtp(code)
        }
      },

      checkCode() {
        return this.code && this.code.length > 0 && this.code.length === 4
      },

      submitInput() {
        if (this.checkCode()) {
          this.activateUser()
        }
      },

      async activateUser(e) {
        this.preloader = true

        let verifyCode = this.code

        verifyCode = verifyCode.toUpperCase()

        this.currentActivationCode = verifyCode

        if (!this.checkCode()) {
          this.$bvToast.toast(this.$t('messages.error.otp-required'), {
            title: this.$t('global.error.title'),
            variant: 'danger',
            solid: true,
          })

          this.preloader = false

          return false
        }

        try {
          const emailVerifyData = await this.$store.dispatch('post_email_verify', {
            email: this.currentEmail,
            code: verifyCode,
          })

          this.$utils.sendEvent('signupEvent', {
            method: 'form',
            status: 'success',
            referral_code: emailVerifyData?.voucher?.code,
            referral_amount: emailVerifyData?.voucher?.amount
              ? parseFloat(emailVerifyData?.voucher?.amount?.amount)
              : undefined,
            currency: emailVerifyData?.voucher?.amount?.currency?.code,
            user_id: emailVerifyData?.user?.id,
            name: emailVerifyData?.user?.name,
            email: emailVerifyData?.user?.email,
          })

          if (emailVerifyData.reward) {
            window.localStorage.setItem('is_first_usage', true)

            window.localStorage.setItem('Airalo.Reward', JSON.stringify(emailVerifyData.reward))
          }

          await this.$auth.loginWith('local', {
            data: {
              email: this.currentEmail,
              password: window.localStorage.getItem('set_auth_dialogs_pwd'),
            },
          })

          await this.$store.dispatch('userSettings/fetch_settings')

          window.localStorage.setItem('set_auth_dialogs_pwd', null)

          // Check if the newly created user has eSIMs. If true, it means they have redeemed an eSIM voucher during signup.
          await this.$store.dispatch('fetch_user_esims')

          const didRedeemEsim = (await this.$store.getters.get_user_esims().length) > 0

          this.$store.commit('set_should_show_signup_reward', didRedeemEsim)

          if (
            emailVerifyData?.voucher?.voucher_type === 'referral' ||
            emailVerifyData?.voucher?.voucher_type === 'airmoney'
          ) {
            this.$store.commit('set_is_first_login', true)
          }

          const redirectURL = localStorage.getItem('redirect-url')

          if (redirectURL) {
            localStorage.removeItem('redirect-url')
            window.location.href = redirectURL
          } else {
            this.$router.push(this.localePath('/'))
          }

          this.preloader = false
        } catch (error) {
          this.preloader = false

          this.$bvToast.toast(error.response.data.message, {
            title: this.$t('global.error.title'),
            variant: 'danger',
            solid: true,
          })
        }
      },

      async resendActivation() {
        this.preloader = true

        try {
          const verifyCode = await this.$axios.post('/auth/verify/code', {
            email: this.currentEmail,
          })

          this.$bvToast.toast(verifyCode.data.message, {
            title: this.$t('scenes.checkout.success'),
            variant: 'success',
            solid: true,
          })

          this.preloader = false
        } catch (error) {
          this.preloader = false

          this.$bvToast.toast(error.response.data.message, {
            title: this.$t('global.error.title'),
            variant: 'danger',
            solid: true,
          })
        }
      },
    },

    mounted() {
      const checkEmail = this.setEmail()

      if (checkEmail) {
        this.setCodeAndActiveUserFromQuery()
      }
    },
  }
</script>
